import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/adminApi";
import { ArrowLeft, Loader2, ChevronDown, Check } from "lucide-react";
import RevealModal from "../components/RevealModal";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry", "Chandigarh",
  "Andaman and Nicobar Islands", "Dadra and Nagar Haveli and Daman and Diu",
  "Lakshadweep",
];

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export default function OnboardTenantPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    tenant_name: "", tenant_state: "", gstin: "", address: "",
    owner_name: "", owner_email: "", owner_temp_password: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [reveal, setReveal] = useState(null);
  const [showStates, setShowStates] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const validate = () => {
    if (!form.tenant_name.trim()) return "Tenant name is required";
    if (!form.tenant_state) return "State is required";
    if (form.gstin && !GSTIN_RE.test(form.gstin.toUpperCase())) return "GSTIN must be 15-character format (e.g. 29ABCDE1234F1Z5)";
    if (!form.owner_name.trim()) return "Owner name is required";
    if (!form.owner_email.trim()) return "Owner email is required";
    if (form.owner_temp_password && form.owner_temp_password.length < 8) return "Password must be at least 8 characters (or leave blank to auto-generate)";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError("");
    try {
      const body = {
        tenant_name: form.tenant_name.trim(),
        tenant_state: form.tenant_state,
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim(),
      };
      if (form.gstin.trim()) body.gstin = form.gstin.trim().toUpperCase();
      if (form.address.trim()) body.address = form.address.trim();
      if (form.owner_temp_password) body.owner_temp_password = form.owner_temp_password;
      const res = await api.post("/tenants/create", body);
      setReveal(res.data);
    } catch (err2) {
      const d = err2.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "EMAIL_TAKEN") {
        setError("Owner email is already registered.");
      } else if (d && typeof d === "object" && d.code === "INVALID_GSTIN") {
        setError("GSTIN format is invalid.");
      } else {
        setError(typeof d === "string" ? d : "Failed to create tenant");
      }
    } finally { setSaving(false); }
  };

  const closeReveal = () => {
    if (reveal?.tenant_id) {
      navigate(`/admin/tenants/${reveal.tenant_id}`);
    } else {
      navigate("/admin/tenants");
    }
    setReveal(null);
  };

  return (
    <div className="p-6 md:p-8 max-w-xl mx-auto glass-animate" data-testid="onboard-tenant-page">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} data-testid="onboard-back" className="p-1.5 hover:bg-[#7C5CFF]/[0.06] rounded-xl">
          <ArrowLeft className="w-5 h-5 text-[#0B0F1A]" />
        </button>
        <h1 className="text-2xl font-extrabold text-[#0B0F1A] tracking-tight">Onboard Tenant</h1>
      </div>

      <form onSubmit={submit} className="glass-card p-5 space-y-4">
        {error && <p className="text-red-600 text-sm bg-red-50 p-2 rounded-xl" data-testid="onboard-error">{error}</p>}

        <Field label="Tenant Name *" testId="onboard-tenant-name" value={form.tenant_name} onChange={(v) => set("tenant_name", v)} placeholder="Ravi Traders" />

        {/* State picker */}
        <div>
          <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">State *</label>
          <div className="relative">
            <button type="button" data-testid="onboard-state-picker" onClick={() => setShowStates(!showStates)}
              className={`w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm text-left flex items-center justify-between focus:border-[#7C5CFF] focus:outline-none ${form.tenant_state ? "text-[#0B0F1A]" : "text-[#5B617A]/50"}`}>
              {form.tenant_state || "Select an Indian state"}
              <ChevronDown className="w-4 h-4 text-[#5B617A]" />
            </button>
            {showStates && (
              <div className="absolute z-50 top-full mt-1 left-0 right-0 glass-dropdown max-h-48 overflow-auto">
                {INDIAN_STATES.map((s) => (
                  <button key={s} type="button" data-testid={`onboard-state-${s.replace(/\s+/g, "-")}`}
                    onClick={() => { set("tenant_state", s); setShowStates(false); }}
                    className="w-full px-3 py-2 text-sm text-left text-[#0B0F1A] hover:bg-[#7C5CFF]/[0.06] flex items-center justify-between">
                    {s}
                    {form.tenant_state === s && <Check className="w-3.5 h-3.5 text-[#7C5CFF]" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <Field label="GSTIN (optional)" testId="onboard-gstin" value={form.gstin} onChange={(v) => set("gstin", v)}
          placeholder="29ABCDE1234F1Z5" className="uppercase" />
        <Field label="Address (optional)" testId="onboard-address" value={form.address} onChange={(v) => set("address", v)} placeholder="123, Main Street" />

        <hr className="border-[#7C5CFF]/[0.06]" />

        <Field label="Owner Name *" testId="onboard-owner-name" value={form.owner_name} onChange={(v) => set("owner_name", v)} placeholder="Ravi Menon" />
        <Field label="Owner Email *" testId="onboard-owner-email" value={form.owner_email} onChange={(v) => set("owner_email", v)} placeholder="ravi@company.com" type="email" />
        <Field label="Temp Password (blank = auto)" testId="onboard-temp-pw" value={form.owner_temp_password} onChange={(v) => set("owner_temp_password", v)} placeholder="Leave blank to auto-generate" type="password" />

        <button data-testid="onboard-submit" type="submit" disabled={saving}
          className="w-full h-10 rounded-xl text-white font-semibold text-sm flex items-center justify-center btn-primary-glass">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Tenant"}
        </button>
      </form>

      {reveal && (
        <RevealModal
          title="Tenant Created"
          subtitle={`${reveal.tenant_name} · ${reveal.owner_email}`}
          fields={[
            { label: "Tenant Name", value: reveal.tenant_name },
            { label: "Owner Email", value: reveal.owner_email, copyable: true },
            { label: "Temporary Password", value: reveal.temporary_password, mono: true, copyable: true },
            { label: "Plan", value: `Trial (expires ${reveal.trial_ends_at?.slice(0, 10)})` },
          ]}
          warning="This password will only be shown once. Share securely."
          onClose={closeReveal}
        />
      )}
    </div>
  );
}

function Field({ label, testId, value, onChange, placeholder, type, className: extra }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">{label}</label>
      <input data-testid={testId} type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm text-[#0B0F1A] glass-input ${extra || ""}`} />
    </div>
  );
}
